import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronDown, AlertTriangle, Clock } from "lucide-react";
import { imageUrl } from "../lib/api";

const MaskedLine = ({ children, delay, className }) => (
  <span className="block overflow-hidden">
    <motion.span
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
      className={`block ${className}`}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const go = (e, href) => {
    e.preventDefault();
    window.__lenis?.scrollTo(href, { offset: -70 });
  };

  return (
    <section id="top" ref={sectionRef} data-testid="hero-section" className="relative flex min-h-[100svh] items-end overflow-hidden">
      <motion.div style={{ y: imgY }} className="absolute inset-0 -bottom-20">
        <img
          src={imageUrl("/api/images/hero-sxo.png")}
          alt="Adult and kid riding electric off-road bikes on a dirt track"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-forest-deep via-forest-deep/40 to-forest-deep/20" />
        <div className="absolute inset-0 bg-gradient-to-r from-forest-deep/70 via-transparent to-transparent" />
        <div className="energy-stripes absolute inset-0" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-16 pt-40 lg:px-8">
        <div className="max-w-3xl">
          <MaskedLine delay={0.3}>
            <span className="mono-label flex items-center gap-3">
              <span className="inline-block h-0.5 w-10 bg-brass" />
              HitWay · Sur-Ron · Talaria · OSET — Pre-order HQ
            </span>
          </MaskedLine>

          <h1 className="mt-6 font-serif text-5xl font-black uppercase leading-[0.95] tracking-tight text-bone sm:text-6xl lg:text-7xl">
            <MaskedLine delay={0.45}>Ride electric.</MaskedLine>
            <MaskedLine delay={0.58}>
              <span className="text-brass">Ride wild.</span>
            </MaskedLine>
            <MaskedLine delay={0.71}>All ages. All mud.</MaskedLine>
          </h1>

          <MaskedLine delay={0.9}>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-sand sm:text-lg">
              The UK's home for electric bikes — road-legal HitWay for the street,
              off-road Sur-Ron, Talaria and OSET for private land. Real photos,
              plain facts, one honest price — UK delivery included.
            </p>
          </MaskedLine>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.1, ease: [0.22, 1, 0.36, 1] }}
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <a
              href="#off-road"
              data-testid="hero-offroad-cta"
              onClick={(e) => go(e, "#off-road")}
              className="clip-corner-sm border-2 border-volt bg-volt/10 px-8 py-[14px] font-mono text-[11px] font-bold tracking-[0.2em] text-volt uppercase transition-colors hover:bg-volt hover:text-forest-deep"
            >
              Off-Road Bikes
            </a>
            <a
              href="#road-legal"
              data-testid="hero-road-legal-cta"
              onClick={(e) => go(e, "#road-legal")}
              className="clip-corner-sm bg-brass px-8 py-4 font-mono text-[11px] font-bold tracking-[0.2em] text-white uppercase transition-colors hover:bg-brass-bright"
            >
              Road Legal Bikes
            </a>
            <span className="flex items-center gap-2 font-mono text-[10px] tracking-[0.15em] text-cyanx uppercase">
              <Clock className="h-3.5 w-3.5" />
              Lead times: 1–4 weeks
            </span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.3 }}
            data-testid="legal-disclaimer-badge"
            className="disclaimer-box mt-10 flex max-w-xl items-start gap-3 px-4 py-3"
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-brass" />
            <p className="font-mono text-[10px] font-medium leading-relaxed tracking-[0.08em] text-bone/90 uppercase">
              Off-road bikes are for private land use only — not road legal. Our road-legal range is EN 15194 certified.
            </p>
          </motion.div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-6 right-8 z-10 hidden items-center gap-3 lg:flex"
      >
        <span className="font-mono text-[10px] tracking-[0.3em] text-sand uppercase">Scroll</span>
        <ChevronDown className="h-4 w-4 animate-bounce text-brass" />
      </motion.div>
    </section>
  );
};
